/**
 * InlineAssignments — compact assignment display for below the page title.
 *
 * Shows: overlapping avatar stack + "+" button (opens Manage Assignments dialog)
 *        + "Assign to me" button if current user is not already assigned.
 *
 * Reuses the same hooks and dialog from DocSidebarAssignmentsSection but renders
 * inline (horizontal row) instead of the sidebar vertical layout.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "./UserAvatar";
import {
  useAssignDoc,
  useUnassignDoc,
  useAssignToMe,
  useAssignUserSuggestions,
  useAssignGroupSuggestions,
} from "../api/useDocAssignment";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import type { DocSidebarAssignment } from "@/lib/erp/docs";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface InlineAssignmentsProps {
  doctype: string;
  name: string;
  assigned_to: DocSidebarAssignment[];
  editable?: boolean;
}

export function InlineAssignments({
  doctype,
  name,
  assigned_to,
  editable = false,
}: InlineAssignmentsProps) {
  const { language } = useLanguage();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.email || currentUser?.name;

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");
  const [groupSearchText, setGroupSearchText] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [priority, setPriority] = useState<"Low" | "Medium" | "High" | "Urgent">("Medium");
  const [completeBy, setCompleteBy] = useState("");
  const [assignmentComment, setAssignmentComment] = useState("");
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);

  const userSearchRef = useRef<HTMLInputElement>(null);
  const userSuggestionsRef = useRef<HTMLDivElement>(null);
  const groupSearchRef = useRef<HTMLInputElement>(null);
  const groupSuggestionsRef = useRef<HTMLDivElement>(null);

  const assignDocMutation = useAssignDoc(doctype, name);
  const unassignDocMutation = useUnassignDoc(doctype, name);
  const assignToMeMutation = useAssignToMe(doctype, name);

  const debouncedUserSearch = useDebounce(userSearchText, 300);
  const debouncedGroupSearch = useDebounce(groupSearchText, 300);

  const { data: userSuggestions = [], isLoading: isLoadingUsers } =
    useAssignUserSuggestions(debouncedUserSearch, {
      enabled: editable,
      isOpen: showUserSuggestions,
      minLength: 0,
    });

  const { data: groupSuggestions = [], isLoading: isLoadingGroups } =
    useAssignGroupSuggestions(debouncedGroupSearch, {
      enabled: editable,
      isOpen: showGroupSuggestions,
      minLength: 0,
    });

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) =>
      getUITranslation(key, language) || fallback;
    return {
      manageAssignments: getUI("manage_assignments", "Manage Assignments"),
      currentAssignments: getUI("current_assignments", "Current Assignments"),
      assignTo: getUI("assign_to", "Assign To"),
      users: getUI("users", "Users"),
      searchUsers: getUI("search_users", "Search users..."),
      userGroups: getUI("user_groups", "User Groups"),
      searchUserGroups: getUI("search_user_groups", "Search user groups..."),
      priority: getUI("priority", "Priority"),
      priorityLow: getUI("priority_low", "Low"),
      priorityMedium: getUI("priority_medium", "Medium"),
      priorityHigh: getUI("priority_high", "High"),
      priorityUrgent: getUI("priority_urgent", "Urgent"),
      comment: getUI("comment", "Comment"),
      addComment: getUI("add_comment", "Add a comment..."),
      assigning: getUI("assigning", "Assigning..."),
      assign: getUI("assign", "Assign"),
      assignToMe: getUI("assign_to_me", "Assign to me"),
      by: getUI("by", "by"),
    };
  }, [language]);

  // Reset dialog state on close
  useEffect(() => {
    if (!showAssignDialog) {
      setUserSearchText("");
      setGroupSearchText("");
      setSelectedUsers([]);
      setSelectedGroups([]);
      setPriority("Medium");
      setCompleteBy("");
      setAssignmentComment("");
      setShowUserSuggestions(false);
      setShowGroupSuggestions(false);
    }
  }, [showAssignDialog]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userSuggestionsRef.current &&
        !userSuggestionsRef.current.contains(event.target as Node) &&
        userSearchRef.current &&
        !userSearchRef.current.contains(event.target as Node)
      ) {
        setShowUserSuggestions(false);
      }
      if (
        groupSuggestionsRef.current &&
        !groupSuggestionsRef.current.contains(event.target as Node) &&
        groupSearchRef.current &&
        !groupSearchRef.current.contains(event.target as Node)
      ) {
        setShowGroupSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAssignDoc = () => {
    if (selectedUsers.length === 0 && selectedGroups.length === 0) return;
    assignDocMutation.mutate(
      {
        users: selectedUsers.length > 0 ? selectedUsers : undefined,
        user_groups: selectedGroups.length > 0 ? selectedGroups : undefined,
        priority,
        complete_by: completeBy || undefined,
        comment: assignmentComment || undefined,
      },
      { onSuccess: () => setShowAssignDialog(false) }
    );
  };

  const handleAssignToMe = () => {
    assignToMeMutation.mutate(
      {
        priority,
        complete_by: completeBy || undefined,
        comment: assignmentComment || undefined,
      },
      { onSuccess: () => setShowAssignDialog(false) }
    );
  };

  const handleUnassignUser = (user: string) => {
    unassignDocMutation.mutate({ user });
  };

  const handleSelectUser = (user: { value: string; label: string }) => {
    if (!selectedUsers.includes(user.value)) {
      setSelectedUsers([...selectedUsers, user.value]);
    }
    setUserSearchText("");
    setShowUserSuggestions(false);
  };

  const handleSelectGroup = (group: { value: string; label: string }) => {
    if (!selectedGroups.includes(group.value)) {
      setSelectedGroups([...selectedGroups, group.value]);
    }
    setGroupSearchText("");
    setShowGroupSuggestions(false);
  };

  const handleRemoveSelectedUser = (user: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u !== user));
  };

  const handleRemoveSelectedGroup = (group: string) => {
    setSelectedGroups(selectedGroups.filter((g) => g !== group));
  };

  const getPriorityBadgeStyle = (p: string) => {
    switch (p) {
      case "Low":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";
      case "Medium":
        return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800";
      case "High":
        return "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800";
      case "Urgent":
        return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";
    }
  };

  const isAssignedToMe = assigned_to.some((a) => a.user === userId);

  if (assigned_to.length === 0 && !editable) return null;

  return (
    <div className="flex items-center gap-3">
      {/* Avatar stack */}
      {assigned_to.length > 0 && (
        <div className="flex -space-x-2.5">
          {assigned_to.slice(0, 5).map((a, index) => (
            <UserAvatar
              key={`${a.user}-${a.assigned_on || index}`}
              imageUrl={a.avatar}
              fallbackText={a.full_name || a.user}
              title={`${a.full_name || a.user} (${a.priority})`}
              size="xl"
              className="border-2 border-background shadow-sm"
            />
          ))}
        </div>
      )}
      {assigned_to.length > 5 && (
        <span className="text-xs font-medium text-muted-foreground">
          +{assigned_to.length - 5}
        </span>
      )}

      {/* "+" button → opens Manage Assignments dialog */}
      {editable && (
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Manage assignments"
            >
              <Plus className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] p-0 flex flex-col overflow-hidden">
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto relative min-h-0">
                <DialogHeader className="px-10 pt-20 pb-4">
                  <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
                    {uiLabels.manageAssignments}
                  </DialogTitle>
                </DialogHeader>

                <div className="pb-8 px-10 pt-2">
                  <div className="space-y-6">
                    {/* Current assignees */}
                    {assigned_to.length > 0 && (
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-foreground px-1">
                          {uiLabels.currentAssignments}
                        </div>
                        <div className="bg-muted rounded-md overflow-hidden border border-border/40">
                          {assigned_to.map((a, index) => (
                            <div
                              key={`${a.user}-${a.assigned_on || index}`}
                              className="flex items-center justify-between min-h-[44px] px-4 py-2.5 border-b border-border/40 last:border-b-0 group"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <UserAvatar
                                  imageUrl={a.avatar}
                                  fallbackText={a.full_name || a.user}
                                  title={a.full_name || a.user}
                                  size="lg"
                                  className="flex-shrink-0"
                                />
                                <div className="flex flex-col gap-0 min-w-0 flex-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[15px] font-normal text-foreground truncate">
                                      {a.full_name || a.user}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-[10px] px-2 py-0.5 font-semibold rounded-full h-5 flex-shrink-0 border",
                                        getPriorityBadgeStyle(a.priority)
                                      )}
                                    >
                                      {a.priority}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
                                    {a.complete_by && (
                                      <div className="flex items-center gap-1 flex-shrink-0 text-blue-600 dark:text-blue-400">
                                        <Calendar className="h-2.5 w-2.5" />
                                        <span className="whitespace-nowrap font-medium">
                                          {new Date(a.complete_by).toLocaleDateString()}
                                        </span>
                                      </div>
                                    )}
                                    {a.assigned_by && (
                                      <span className="truncate">
                                        {uiLabels.by} {a.assigned_by}
                                      </span>
                                    )}
                                    {a.comment && (
                                      <span className="truncate text-muted-foreground/60">
                                        &bull; {a.comment}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-sm touch-manipulation flex-shrink-0 opacity-0 group-hover:opacity-100 ml-2"
                                onClick={() => handleUnassignUser(a.user)}
                                disabled={unassignDocMutation.isPending}
                                aria-label={`Remove ${a.full_name || a.user}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add assignments */}
                    <div className="space-y-4 pt-6 border-t border-border/50">
                      <div className="text-sm font-medium text-foreground px-1">
                        {uiLabels.assignTo}
                      </div>

                      {/* Selected users */}
                      {selectedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2.5 sm:gap-2">
                          {selectedUsers.map((user) => {
                            const s = userSuggestions.find((u) => u.value === user);
                            return (
                              <Badge
                                key={user}
                                variant="outline"
                                className="rounded-full px-4 py-2 sm:px-3.5 sm:py-1.5 text-sm sm:text-xs flex items-center gap-2 sm:gap-1.5 bg-background/50 border-border/50 shadow-[0_1px_3px_rgba(0,0,0,0.06)] min-h-[44px] sm:min-h-0 touch-manipulation"
                              >
                                <span className="font-medium">{s?.label || user}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSelectedUser(user)}
                                  className="ml-0.5 rounded-full p-2 sm:p-1.5 touch-manipulation min-h-[36px] min-w-[36px] sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center"
                                  aria-label={`Remove ${s?.label || user}`}
                                >
                                  <X className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground/70 hover:text-foreground transition-colors" />
                                </button>
                              </Badge>
                            );
                          })}
                        </div>
                      )}

                      {/* Selected groups */}
                      {selectedGroups.length > 0 && (
                        <div className="flex flex-wrap gap-2.5 sm:gap-2">
                          {selectedGroups.map((group) => (
                            <Badge
                              key={group}
                              variant="outline"
                              className="rounded-full px-4 py-2 sm:px-3.5 sm:py-1.5 text-sm sm:text-xs flex items-center gap-2 sm:gap-1.5 bg-background/50 border-border/50 shadow-[0_1px_3px_rgba(0,0,0,0.06)] min-h-[44px] sm:min-h-0 touch-manipulation"
                            >
                              <span className="font-medium">{group}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSelectedGroup(group)}
                                className="ml-0.5 rounded-full p-2 sm:p-1.5 touch-manipulation min-h-[36px] min-w-[36px] sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center"
                                aria-label={`Remove ${group}`}
                              >
                                <X className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground/70 hover:text-foreground transition-colors" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Form fields container */}
                      <div className="bg-muted rounded-md overflow-hidden border border-border/40">
                        {/* User search */}
                        <div className="relative flex items-center justify-between min-h-[52px] px-5 border-b border-border/40">
                          <label className="text-[17px] text-foreground font-normal flex-shrink-0">
                            {uiLabels.users}
                          </label>
                          <div className="flex-1 flex justify-end">
                            <div className="relative" style={{ width: "300px", maxWidth: "100%" }}>
                              <div className="relative">
                                <Input
                                  ref={userSearchRef}
                                  placeholder={uiLabels.searchUsers}
                                  value={userSearchText}
                                  onChange={(e) => {
                                    setUserSearchText(e.target.value);
                                    setShowUserSuggestions(true);
                                  }}
                                  onFocus={() => setShowUserSuggestions(true)}
                                  className="h-[52px] px-5 text-[17px] text-foreground font-normal bg-transparent border-0 rounded-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground text-right !shadow-none w-full pr-12"
                                  style={{ boxShadow: "none", borderColor: "transparent", border: "0" }}
                                />
                                {selectedUsers.length > 0 && (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <Badge
                                      variant="secondary"
                                      className="h-6 px-2 text-xs font-semibold bg-primary text-primary-foreground border-0 shadow-sm rounded-full min-w-[24px] flex items-center justify-center"
                                    >
                                      {selectedUsers.length}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                              {showUserSuggestions && (
                                <div
                                  ref={userSuggestionsRef}
                                  className="absolute z-50 right-0 w-full mt-1 bg-background border border-border rounded-sm shadow-lg max-h-96 overflow-auto min-w-[280px]"
                                >
                                  {isLoadingUsers && (
                                    <div className="px-5 py-4 text-sm text-muted-foreground">Loading users...</div>
                                  )}
                                  {!isLoadingUsers &&
                                    userSuggestions
                                      .filter((s) => !selectedUsers.includes(s.value))
                                      .slice(0, 10)
                                      .map((user) => (
                                        <button
                                          key={user.value}
                                          type="button"
                                          onClick={() => handleSelectUser(user)}
                                          className="w-full text-left px-5 py-4 md:py-3.5 text-base md:text-sm flex items-center gap-3 md:gap-2 touch-manipulation min-h-[56px] md:min-h-[52px]"
                                        >
                                          {user.avatar && (
                                            <UserAvatar
                                              imageUrl={user.avatar}
                                              fallbackText={user.label}
                                              size="md"
                                              className="flex-shrink-0"
                                            />
                                          )}
                                          <span className="font-medium">{user.label}</span>
                                        </button>
                                      ))}
                                  {!isLoadingUsers && userSuggestions.length === 0 && userSearchText.length > 0 && (
                                    <div className="px-5 py-4 text-sm text-muted-foreground">No matching users</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Group search */}
                        <div className="relative flex items-center justify-between min-h-[52px] px-5 border-b border-border/40">
                          <label className="text-[17px] text-foreground font-normal flex-shrink-0">
                            {uiLabels.userGroups}
                          </label>
                          <div className="flex-1 flex justify-end">
                            <div className="relative" style={{ width: "300px", maxWidth: "100%" }}>
                              <div className="relative">
                                <Input
                                  ref={groupSearchRef}
                                  placeholder={uiLabels.searchUserGroups}
                                  value={groupSearchText}
                                  onChange={(e) => {
                                    setGroupSearchText(e.target.value);
                                    setShowGroupSuggestions(true);
                                  }}
                                  onFocus={() => setShowGroupSuggestions(true)}
                                  className="h-[52px] px-5 text-[17px] text-foreground font-normal bg-transparent border-0 rounded-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground text-right !shadow-none w-full pr-12"
                                  style={{ boxShadow: "none", borderColor: "transparent", border: "0" }}
                                />
                                {selectedGroups.length > 0 && (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <Badge
                                      variant="secondary"
                                      className="h-6 px-2 text-xs font-semibold bg-primary text-primary-foreground border-0 shadow-sm rounded-full min-w-[24px] flex items-center justify-center"
                                    >
                                      {selectedGroups.length}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                              {showGroupSuggestions && (
                                <div
                                  ref={groupSuggestionsRef}
                                  className="absolute z-50 right-0 w-full mt-1 bg-background border border-border rounded-sm shadow-lg max-h-96 overflow-auto min-w-[280px]"
                                >
                                  {isLoadingGroups && (
                                    <div className="px-5 py-4 text-sm text-muted-foreground">Loading groups...</div>
                                  )}
                                  {!isLoadingGroups &&
                                    groupSuggestions
                                      .filter((s) => !selectedGroups.includes(s.value))
                                      .slice(0, 10)
                                      .map((group) => (
                                        <button
                                          key={group.value}
                                          type="button"
                                          onClick={() => handleSelectGroup(group)}
                                          className="w-full text-left px-5 py-4 md:py-3.5 text-base md:text-sm flex items-center gap-3 md:gap-2 touch-manipulation min-h-[56px] md:min-h-[52px]"
                                        >
                                          <span className="font-medium">{group.label}</span>
                                        </button>
                                      ))}
                                  {!isLoadingGroups && groupSuggestions.length === 0 && groupSearchText.length > 0 && (
                                    <div className="px-5 py-4 text-sm text-muted-foreground">No matching groups</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Priority */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-border/40">
                          <label className="text-[17px] text-foreground font-normal flex-shrink-0">
                            {uiLabels.priority}
                          </label>
                          <div className="flex-1 flex justify-end">
                            <div style={{ width: "200px", maxWidth: "100%" }}>
                              <Select
                                value={priority}
                                onValueChange={(v: "Low" | "Medium" | "High" | "Urgent") => setPriority(v)}
                              >
                                <SelectTrigger className="h-[52px] px-5 text-[17px] text-foreground font-normal bg-transparent border-0 rounded-none focus:outline-none !shadow-none justify-end [&>span]:text-right [&>span]:flex [&>span]:justify-end w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent align="end" sideOffset={4}>
                                  <SelectItem value="Low">{uiLabels.priorityLow}</SelectItem>
                                  <SelectItem value="Medium">{uiLabels.priorityMedium}</SelectItem>
                                  <SelectItem value="High">{uiLabels.priorityHigh}</SelectItem>
                                  <SelectItem value="Urgent">{uiLabels.priorityUrgent}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Complete by */}
                        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-border/40">
                          <label className="text-[17px] text-foreground font-normal flex-shrink-0">
                            Complete By
                          </label>
                          <div className="flex-1 flex justify-end">
                            <div style={{ width: "200px", maxWidth: "100%" }}>
                              <Input
                                type="date"
                                value={completeBy}
                                onChange={(e) => setCompleteBy(e.target.value)}
                                className="h-[52px] px-5 text-[17px] text-foreground font-normal bg-transparent border-0 rounded-none focus:outline-none !shadow-none [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer w-full"
                                style={{ textAlign: "right", boxShadow: "none", borderColor: "transparent", border: "0" }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Comment */}
                        <div className="flex items-start justify-between min-h-[120px] px-5 py-4">
                          <label className="text-[17px] text-foreground font-normal flex-shrink-0 pt-2">
                            {uiLabels.comment}
                          </label>
                          <div className="flex-1 flex justify-end">
                            <div style={{ width: "300px", maxWidth: "100%" }}>
                              <Textarea
                                value={assignmentComment}
                                onChange={(e) => setAssignmentComment(e.target.value)}
                                placeholder={uiLabels.addComment}
                                className="min-h-[120px] px-5 py-4 text-[17px] text-foreground font-normal bg-transparent border-0 rounded-none focus:outline-none resize-none placeholder:text-muted-foreground text-right !shadow-none w-full"
                                style={{ boxShadow: "none", borderColor: "transparent", border: "0" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom fade gradient */}
                <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-none" />
              </div>

              {/* Sticky Assign Button */}
              <div className="flex justify-center px-10 py-6 bg-background border-t border-border/50 flex-shrink-0">
                <Button
                  type="button"
                  onClick={handleAssignDoc}
                  disabled={
                    (selectedUsers.length === 0 && selectedGroups.length === 0) ||
                    assignDocMutation.isPending
                  }
                  className="w-full text-[17px] font-semibold bg-primary hover:bg-primary/90 active:bg-primary/80 text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignDocMutation.isPending ? uiLabels.assigning : uiLabels.assign}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* "Assign to me" button */}
      {editable && userId && !isAssignedToMe && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={handleAssignToMe}
          disabled={assignToMeMutation.isPending}
        >
          {assignToMeMutation.isPending ? uiLabels.assigning : uiLabels.assignToMe}
        </Button>
      )}
    </div>
  );
}
